import {useCallback, useRef, useState} from 'react';

const FFT_SIZE = 256;

const getSupportedMimeType = (): string => {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/ogg'];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
};

export interface UseAudioRecorderReturn {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  recordedBlob: Blob | null;
  recordingTime: number;
  analyserNode: AnalyserNode | null;
  isPermissionDenied: boolean;
}

export const useAudioRecorder = (): UseAudioRecorderReturn => {
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mimeTypeRef = useRef<string>('');

  const cleanup = useCallback((): void => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void audioContextRef.current?.close();
    audioContextRef.current = null;
    setAnalyserNode(null);
  }, []);

  const startRecording = useCallback(async (): Promise<void> => {
    setRecordedBlob(null);
    setRecordingTime(0);
    chunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({audio: true});
      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      analyser.smoothingTimeConstant = 0.5;
      audioContext.createMediaStreamSource(stream).connect(analyser);
      setAnalyserNode(analyser);

      const mimeType = getSupportedMimeType();
      const mediaRecorder = new MediaRecorder(stream, mimeType ? {mimeType} : undefined);
      mimeTypeRef.current = mediaRecorder.mimeType || mimeType;
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e: BlobEvent): void => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = (): void => {
        const blob = new Blob(chunksRef.current, {type: mimeTypeRef.current || 'audio/webm'});
        setRecordedBlob(blob);
        cleanup();
      };

      recordingStartRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setRecordingTime(Date.now() - recordingStartRef.current);
      }, 100);

      mediaRecorder.start(100);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotAllowedError') {
        setIsPermissionDenied(true);
      }
      cleanup();
    }
  }, [cleanup]);

  const stopRecording = useCallback((): void => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  return {startRecording, stopRecording, recordedBlob, recordingTime, analyserNode, isPermissionDenied};
};
